'use client'

import { useMemo, useState } from 'react'
import { ToolField, ToolResultCard, ToolResultRow, ToolSelect, toolCurrency, toolFmt, toolNum } from './tool-inputs'

type BrickType = 'Standard' | 'Maxi Brick' | 'Block'

// Same spec table as the in-app brick & block calculator.
const BRICK_SPECS: Record<BrickType, { perM2: number; mortarM3PerM2: number }> = {
  Standard: { perM2: 55, mortarM3PerM2: 0.025 },
  'Maxi Brick': { perM2: 33, mortarM3PerM2: 0.030 },
  Block: { perM2: 12.5, mortarM3PerM2: 0.010 },
}

export function BrickCalculatorTool() {
  const [length, setLength] = useState('10')
  const [height, setHeight] = useState('2.4')
  const [brickType, setBrickType] = useState<BrickType>('Standard')
  const [waste, setWaste] = useState('5')
  const [price, setPrice] = useState('3.50')

  const r = useMemo(() => {
    const spec = BRICK_SPECS[brickType]
    const area = toolNum(length) * toolNum(height)
    const areaWithWaste = area * (1 + toolNum(waste) / 100)
    const units = Math.ceil(areaWithWaste * spec.perM2)
    const mortarVolM3 = area * spec.mortarM3PerM2
    const cementBags = Math.ceil(mortarVolM3 / 7 / 0.035)
    const sandTons = parseFloat((mortarVolM3 * 6 / 7 * 1.6).toFixed(2))
    const cost = units * toolNum(price)
    return { area, units, cementBags, sandTons, cost }
  }, [length, height, brickType, waste, price])

  const unitLabel = brickType === 'Block' ? 'blocks' : 'bricks'

  return (
    <div className="grid gap-6 md:grid-cols-[1fr_380px] md:items-start">
      <div className="space-y-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Wall</h2>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <ToolField label="Length" value={length} onChange={setLength} unit="m" />
            <ToolField label="Height" value={height} onChange={setHeight} unit="m" />
          </div>
          <div className="mt-3">
            <ToolSelect
              label="Brick / block type"
              value={brickType}
              onChange={(v) => setBrickType(v as BrickType)}
              options={[
                { value: 'Standard', label: 'Standard brick (55/m²)' },
                { value: 'Maxi Brick', label: 'Maxi brick (33/m²)' },
                { value: 'Block', label: 'Block (12.5/m²)' },
              ]}
            />
          </div>
        </div>
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Waste &amp; price</h2>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <ToolField label="Waste allowance" value={waste} onChange={setWaste} unit="%" />
            <ToolField label={`Price per ${unitLabel.slice(0, -1)}`} value={price} onChange={setPrice} unit="R" step="0.5" />
          </div>
        </div>
      </div>

      <ToolResultCard title="Your brickwork estimate">
        <ToolResultRow label="Wall area" value={`${toolFmt(r.area, 1)} m²`} />
        <ToolResultRow label={`${unitLabel[0].toUpperCase() + unitLabel.slice(1)} needed`} value={`${r.units.toLocaleString('en-ZA')} ${unitLabel}`} />
        <ToolResultRow label="Cement bags (mortar)" value={`${r.cementBags} bags`} />
        <ToolResultRow label="Building sand" value={`${toolFmt(r.sandTons, 2)} t`} />
        <ToolResultRow label={`Estimated ${unitLabel} cost`} value={toolCurrency(r.cost)} strong />
      </ToolResultCard>
    </div>
  )
}
